import { FC, ReactNode, useEffect } from "react";
import { useParams, useLocation, Outlet, useNavigate } from "react-router-dom";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { useGetCompanyEx, useGoToCompany } from "../../api/CompaniesApi";
import LoadingSpinner from "../../shared-components/LoadingSpinner";
import ErrorMessageContainer from "../../shared-components/ErrorMessageContainer";
import SettingsNav from "./SettingsNav";

interface SettingsLayoutProps {
  children?: ReactNode;
}

const SettingsLayout: FC<SettingsLayoutProps> = ({ children }) => {
  const { companyId, subid } = useParams();
  const location = useLocation();
  const path = location.pathname;
  const navigate = useNavigate();
  const { goToCompany, goToSubCompany } = useGoToCompany();
  const {
    isLoading: isLoadingCompany,
    data: companyData,
    mutate: loadCompany,
    error: errorLoadingCompany,
  } = useGetCompanyEx();

  const {
    isLoading: isLoadingSubCompany,
    data: subCompany,
    mutate: loadSubCompany,
    error: errorLoadingSubCompany,
  } = useGetCompanyEx();

  useEffect(() => {
    if (companyId) {
      loadCompany(companyId);
    }
    if (subid) {
      loadSubCompany(subid);
    }
  }, [companyId, subid, loadCompany, loadSubCompany]);

  if (isLoadingCompany || isLoadingSubCompany) {
    return <LoadingSpinner />;
  }

  if (errorLoadingCompany || errorLoadingSubCompany) {
    return <ErrorMessageContainer message="Error loading company" />;
  }

  // Get the current section name from the URL
  const getSettingsSectionName = () => {
    if (path.includes("/settings/subscriptions"))
      return "Screening Subscriptions";
    if (path.includes("/settings/api")) return "API Settings";
    if (path.includes("/settings/reports")) return "Report Settings";
    if (path.includes("/settings/report-types")) return "Report Types";
    if (path.includes("/settings/behavior-kits")) return "Behavior Kits";
    if (path.includes("/settings/integrations")) return "Integrations";
    if (path.includes("/settings/checktypes")) return "Check Types";
    if (path.includes("/subcompanies")) return "Sub Company List";
    return subid ? "Sub Company Settings" : "Company Settings";
  };

  const isInSubcompaniesList = path.includes("subcompanies");

  function backNavigation() {
    if (isInSubcompaniesList) {
      goToCompany(companyId ?? "", "behavior-kits");
    } else if (subid) {
      goToCompany(companyId ?? "", "subcompanies", true);
    } else {
      navigate(`/companies`);
    }
  }

  return (
    <div>
      <div className="text-gray-900">
        <div
          className="cursor-pointer flex items-center gap-2 mb-4"
          onClick={backNavigation}
        >
          <ChevronLeftIcon className="w-6 h-6" />
          {isInSubcompaniesList ? "Back to Parent Company" : "Back to List"}
        </div>
        <div className="text-4xl mb-4 font-bold ">
          {isInSubcompaniesList && "Sub Company List"}
          {!isInSubcompaniesList && <>{subid && "Sub "}Company Edit</>}
        </div>
      </div>
      {/* Header */}
      <div className="flex items-center gap-2 text-xl text-gray-500">
        <span
          className="hover:underline cursor-pointer"
          onClick={() => {
            goToCompany(companyId ?? "", "behavior-kits");
          }}
        >
          {companyData?.name}
        </span>
        {subid && (
          <>
            <ChevronRightIcon className="w-6 h-6" />
            <span
              className="hover:underline cursor-pointer"
              onClick={() =>
                goToSubCompany(companyId ?? "", subid, "behavior-kits")
              }
            >
              {subCompany?.name}
            </span>
          </>
        )}
        <ChevronRightIcon className="w-6 h-6" />
        <span>{subid && "Sub "}Company Edit</span>
        <ChevronRightIcon className="w-6 h-6" />
        <span className="text-gray-700">{getSettingsSectionName()}</span>
      </div>

      {/* main container */}
      <div className="flex gap-4 bg-white rounded-lg mt-5 min-h-[870px] border border-gray-200">
        {!isInSubcompaniesList && <SettingsNav isSubCompany={!!subid} />}
        <div className="flex flex-col gap-4 flex-1 p-4">
          {children || <Outlet />}
        </div>
      </div>
    </div>
  );
};

export default SettingsLayout;
